import React, {FC, useCallback, useState} from 'react'
import {Modal} from 'react-bootstrap'
import UploadContactHeader from './Element/UploadContactHeader'
import InputStep from './Input'
import InputMapping from './InputMapping'
import Review from './Review'
import {
  usePutUploadContactsMutation,
  usePutUploadCustomContactsMutation,
} from '../../../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import SetOptions from './SetOptions'
import {SelectOption} from '../../../../../types'
import Confirmation from './Confirmation'
import {ContactOpenPopup} from '../../../../../Components/PopupSlice/popupSlice'
import {useDispatch} from 'react-redux'
import {useLazyGetGroupQuery} from '../../../../../../services/cellcast'

export type CsvRow = string[]

export enum UploadStep {
  Input = 'input',
  InputMapping = 'input-mapping',
  Review = 'review',
  SetOptions = 'set-options',
  SetOptionTag = 'set-option-tag',
  SetOptionList = 'set-option-list',
  Confirmation = 'confirmation',
}

export enum InputStepMode {
  Upload = 'upload',
  CopyPaste = 'copyPaste',
  AddSingleContact = 'addSingleContact'
}

export type InputStepErr = {
  upload: string
  copyByDetails: string
  copyByNumber: string
}

export enum InputStepInputCopyPasteType {
  Number = 'number',
  Custom = 'custom',
}

export type UploadPayload = {
  name: string
  file?: File
  mergeLabels: string
  tagIds: string
  unSubList: boolean
}

export type DataMap = {
  oldLabel: string
  newLabel: string
}

export type InputConfirmation = {
  allowPermission: boolean
  allowUpdateProfile: boolean
}

export type UploadContactProps = {
  show: boolean
  isUnsubscribe?: boolean
  listId?: string
  listName?: string
  groupId: string | undefined
  handleClose: () => void
  showAddSingleContact?: () => void
}

export const DefaultInputStepErr: InputStepErr = {
  upload: '',
  copyByDetails: '',
  copyByNumber: '',
}
const UploadContact: FC<UploadContactProps> = (props) => {
  const {show, groupId, listId, listName, isUnsubscribe, handleClose, showAddSingleContact} = props
  const [activeState, setActiveState] = useState<UploadStep>(UploadStep.Input)

  const [getGroup] = useLazyGetGroupQuery()

  const [inputMode, setInputMode] = useState<InputStepMode>(InputStepMode.Upload)
  const [copyPasteType, setCopyPasteType] = useState<InputStepInputCopyPasteType>(
    InputStepInputCopyPasteType.Number
  )
  const [errorInputModeErr, setInputModeErr] = useState<InputStepErr>(DefaultInputStepErr)

  const [finalData, setFinalData] = useState<UploadPayload>({
    name: '',
    mergeLabels: '',
    tagIds: '',
    unSubList: isUnsubscribe || false,
  })

  const disptach = useDispatch()

  const [csvData, setCsvData] = useState<CsvRow[]>([])
  const [customData, setCustomData] = useState<string>('')
  const [numberData, setNumberData] = useState<string>('')
  const [reports, setReports] = useState<any>()
  const [mapped, setMapped] = useState<DataMap[]>([])
  const [inputConfirmationCheck, setInputConfirmationCheck] = useState<InputConfirmation>({
    allowPermission: false,
    allowUpdateProfile: false,
  })
  const [selectedTags, setSelectedTags] = useState<SelectOption<string>[]>([])
  const [selectedList, setSelectedList] = useState<SelectOption<string>[]>([])
  const [submitErr, setSubmitErr] = useState('')

  const [uploadContactsV2, resultUploadContacts] = usePutUploadContactsMutation()
  const [uploadCustomContactsV2, resultUploadCustomContacts] = usePutUploadCustomContactsMutation()

  const Breadcrumbs = () => {
    const baseSteps = [
      { label: "Upload file", condition: true, step:  UploadStep.Input},
      { label: "Match data", condition: inputMode === InputStepMode.Upload, step: UploadStep.InputMapping },
      { label: "Add tag", condition: true, step: UploadStep.SetOptionTag },
      { label: "Add to list", condition: !listId, step: UploadStep.SetOptionList},
      { label: "Summary + Confirmation", condition: true, step: UploadStep.Confirmation },
    ];

    const filteredSteps = baseSteps.filter(step => step.condition);

    const state = activeState === UploadStep.Review ? UploadStep.Confirmation : activeState
    const activeStepIndex = filteredSteps.findIndex((s) => s.step === state);

    const steps = filteredSteps.map((step, index) => ({ ...step, stepNumber: index + 1 }));

    return (
      <div className="d-flex justify-content-end align-items-center px-8 py-2 mt-3">
        <div className="d-flex justify-content-between w-100 px-8 py-2">
          {steps.map((item, index, arr) => {
            const isActive = activeStepIndex === index;
            const isCompleted = activeStepIndex > index;
            const isLast = index === arr.length - 1;

            return (
              <div
                key={item.stepNumber}
                className="d-flex flex-column align-items-center justify-content-start flex-fill position-relative"
                style={{ minWidth: `${100 / steps.length}%`, textAlign: "center" }}
              >
                {!isLast && (
                  <div
                    className="position-absolute start-50 translate-middle-y"
                    style={{
                      top: '22.5%',
                      left: "50%",
                      width: "100%",
                      height: "2px",
                      backgroundColor: isCompleted ? "var(--bs-primary)" : "#e4e6ef",
                      zIndex: 0,
                    }}
                  />
                )}

                <span
                  className={`badge rounded-pill d-flex align-items-center justify-content-center mb-2 position-relative ${
                    isActive
                      ? "bg-primary text-white"
                      : isCompleted
                      ? "bg-success text-white"
                      : "bg-light text-gray-600"
                  }`}
                  style={{
                    fontSize: "0.8rem",
                    width: "2rem",
                    height: "2rem",
                    zIndex: 1,
                  }}
                >
                  {isCompleted ? "✓" : item.stepNumber}
                </span>

                <span
                  className={`fw-semibold ${
                    isActive
                      ? "text-primary"
                      : isCompleted
                      ? "text-success"
                      : "text-gray-600"
                  }`}
                  style={{ fontSize: "0.9rem" }}
                >
                  {item.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    )
  }
  const getModalTitle = () => {
    switch (activeState) {
      case UploadStep.Input:
        if (listId && listName) {
          return `Add Contacts to list - ${listName}`
        }

        if (isUnsubscribe && isUnsubscribe === true) {
          return `Add Contacts Unsubscribe`
        }
        return `Add Contacts`
      case UploadStep.InputMapping:
        return `Map Custom Data`
      case UploadStep.SetOptionTag:
        return `Do you want to add your Contacts to a Tag?`
      case UploadStep.SetOptionList:
        return `Do you want to add or create a specific list for your Contacts?`
      case UploadStep.Confirmation:
        return `Upload Confirmation`
      case UploadStep.Review:
        return `Review Contact Upload`
      default:
        return 'Unknown Action'
    }
  }

  const closeModal = () => {
    setActiveState(UploadStep.Input)
    setCsvData([])
    setCustomData('')
    setNumberData('')
    setFinalData({
      name: '',
      mergeLabels: '',
      tagIds: '',
      unSubList: isUnsubscribe || false,
    })
    setInputConfirmationCheck({
      allowPermission: false,
      allowUpdateProfile: false,
    })
    setInputMode(InputStepMode.Upload)

    if (handleClose) {
      handleClose()
    }
  }

  const handleSubmitReview = useCallback(
    (data: FormData) => {
      uploadContactsV2({
        data,
        id: groupId,
      }).then((res) => {
        if ('data' in res) {
          if (res.data?.error && Object.keys(res.data?.error).length > 0) {
            setSubmitErr(res?.data?.message)
          } else {
            setReports(res)
            toast.success(res?.data?.message)
            setActiveState(UploadStep.Review)
          }
        } else if ('error' in res) {
          toast.error(res.error)
        }
      })
    },
    [groupId, uploadContactsV2]
  )

  const handleSubmitReviewCustomContacts = useCallback(
    (data: FormData) => {
      uploadCustomContactsV2({
        data,
        id: groupId,
      }).then((res) => {
        if ('data' in res) {
          if (res.data?.error && Object.keys(res.data?.error).length > 0) {
            setSubmitErr(res?.data?.message)
          } else {
            setReports(res)
            toast.success(res?.data?.message)
            setActiveState(UploadStep.Review)
          }
        } else if ('error' in res) {
          toast.error(res.error)
        }
      })
    },
    [groupId, uploadContactsV2]
  )

  const buildTagsPayload = useCallback(() => {
    const allSelectedTags = [...selectedTags, ...selectedList]
    const selectedTagsIDs = allSelectedTags.filter((el) => el.value !== '').map((el) => el.value)
    if (listId) {
      selectedTagsIDs.push(listId)
    }
    return selectedTagsIDs || []
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedList, selectedTags, listId])

  const buildFileUploadPayload = useCallback(
    (validate: boolean = false) => {
      let data = new FormData()
      const tagIds = buildTagsPayload()
      const buildPayload: any = {
        name: finalData?.name,
        unSubList: finalData?.unSubList,
        file: finalData?.file,
        mergeLabels: JSON.stringify(mapped),
        tagIds: JSON.stringify([...tagIds]),
        validate,
      }

      for (var key in buildPayload) {
        data.append(key, buildPayload[key])
      }
      return data
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildTagsPayload, finalData?.file, finalData?.name, finalData?.unSubList, mapped]
  )

  const buildCopyPastePayload = useCallback(
    (validate: boolean = false) => {
      const tagIds = buildTagsPayload()
      let formValues: any = {
        campaignUpload: false,
        tagIds: JSON.stringify([...tagIds]),
        unSubList: finalData?.unSubList,
        validate,
      }

      if (copyPasteType === InputStepInputCopyPasteType.Number) {
        formValues = {...formValues, uploadData: numberData, type: 'only-number'}
      }

      if (copyPasteType === InputStepInputCopyPasteType.Custom) {
        formValues = {...formValues, uploadData: customData, type: 'custom-fields'}
      }

      const formData = new FormData()

      for (var key in formValues) {
        formData.append(key, formValues[key])
      }

      return formData
    },
    [buildTagsPayload, copyPasteType, customData, finalData?.unSubList, numberData]
  )

  // Next Step Function
  const handleInputNext = useCallback(() => {
    if (inputMode === InputStepMode.Upload && csvData.length === 0) {
      setInputModeErr({
        upload: 'file can`t be empty',
        copyByDetails: '',
        copyByNumber: '',
      })
      return
    } else {
      if (inputMode === InputStepMode.AddSingleContact) {
        closeModal()
        showAddSingleContact && showAddSingleContact()
        return
      }
  
      if (copyPasteType === InputStepInputCopyPasteType.Number && numberData.trim() === '') {
        setInputModeErr({
          upload: '',
          copyByDetails: '',
          copyByNumber: 'number can`t be empty',
        })
      }
      if (copyPasteType === InputStepInputCopyPasteType.Custom && customData.trim() === '') {
        setInputModeErr({
          upload: '',
          copyByDetails: '',
          copyByNumber: 'custom data can`t be empty',
        })
      }
    }

    if (csvData.length > 0) {
      setActiveState(UploadStep.InputMapping)
      return
    }

    setActiveState(UploadStep.SetOptionTag)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyPasteType, csvData.length, inputMode, numberData])

  const handleMapDataNext = () => {
    setActiveState(UploadStep.SetOptionTag)
  }

  const handleConfirmationNext = () => {
    setSubmitErr('')
    let data: FormData
    if (inputMode === InputStepMode.Upload && csvData.length !== 0) {
      data = buildFileUploadPayload(true)
      handleSubmitReview(data)
    } else {
      data = buildCopyPastePayload(true)
      handleSubmitReviewCustomContacts(data)
    }
  }

  const handleSetOptionBack = () => {
    if (activeState === UploadStep.SetOptionList) {
      setActiveState(UploadStep.SetOptionTag)
    } else if (activeState === UploadStep.Confirmation) {
      if (listId) {
        setActiveState(UploadStep.SetOptionTag)
      } else {
        setActiveState(UploadStep.SetOptionList)
      }
    } else {
      if (csvData.length > 0) {
        setActiveState(UploadStep.InputMapping)
      } else {
        setActiveState(UploadStep.Input)
      }
    }
  }

  const handleSetOptionNext = () => {
    if (activeState === UploadStep.SetOptionTag && !listId) {
      setActiveState(UploadStep.SetOptionList)
    } else if (isUnsubscribe) {
      // If we're on SetOptionList or SetOptionTag and it's unsubscribe, trigger calculation before Review
      handleConfirmationNext()
      setActiveState(UploadStep.Review)
    } else {
      setActiveState(UploadStep.Confirmation)
    }
  }

  const handleReviewNext = () => {
    setSubmitErr('')
    let data: FormData
    let upload
    if (inputMode === InputStepMode.Upload && csvData.length !== 0) {
      data = buildFileUploadPayload(false)
      upload = uploadContactsV2({
        data,
        id: groupId,
      })
    } else {
      data = buildCopyPastePayload(false)
      upload = uploadCustomContactsV2({
        data,
        id: groupId,
      })
    }

    // Handle when unsubscribe
    if (isUnsubscribe) {
      data?.set('unSubList', 'true')
    }

    upload
      .then((res) => {
        if ('data' in res) {
          if (res.data?.error && Object.keys(res.data?.error).length > 0) {
            setSubmitErr(res?.data?.message)
          } else {
            toast.success(res?.data?.message)
            disptach(ContactOpenPopup())
            closeModal()
          }
        } else if ('error' in res) {
          toast.error(res.error)
        }
      })
      .finally(() => {
        getGroup(null)
      })
  }

  const csvLength = csvData.length

  return (
    <Modal
      id='kt_modal_create_app'
      tabIndex={1}
      aria-hidden='true'
      dialogClassName='modal-dialog-centered mw-700px'
      className='vh-100'
      show={show}
      onHide={closeModal}
      onBackdropClick={closeModal}
    >
      <UploadContactHeader title={getModalTitle()} handleClose={closeModal} />
      <Breadcrumbs />

      {activeState === UploadStep.Input && (
        <InputStep
          copyPasteType={copyPasteType}
          customData={customData}
          cswData={csvData}
          error={errorInputModeErr}
          finalData={finalData}
          inputMode={inputMode}
          isLoading={resultUploadContacts.isLoading || resultUploadCustomContacts.isLoading}
          numberData={numberData}
          isUnsubscribe={isUnsubscribe}
          setCopyPasteType={setCopyPasteType}
          setCsvData={setCsvData}
          setCustomData={setCustomData}
          setError={setInputModeErr}
          setFinalData={setFinalData}
          setInputMode={setInputMode}
          setNumberData={setNumberData}
          handleClose={closeModal}
          handleNext={handleInputNext}
        />
      )}
      {activeState === UploadStep.InputMapping && (
        <InputMapping
          mapped={mapped}
          setMapped={setMapped}
          csvData={csvData}
          handleClose={closeModal}
          setActiveState={setActiveState}
          handleNext={handleMapDataNext}
        />
      )}
      {activeState === UploadStep.SetOptionTag && (
        <SetOptions
          activeState={activeState}
          isLoading={resultUploadContacts.isLoading || resultUploadCustomContacts.isLoading}
          selectedList={selectedList}
          selectedTags={selectedTags}
          showList={false}
          showTag={true}
          setActiveState={setActiveState}
          setFinalData={setFinalData}
          setSelectedList={setSelectedList}
          setSelectedTags={setSelectedTags}
          handleClose={closeModal}
          handleBack={handleSetOptionBack}
          handleNext={handleSetOptionNext}
        />
      )}
      {activeState === UploadStep.SetOptionList && !listId && (
        <SetOptions
          activeState={activeState}
          isLoading={resultUploadContacts.isLoading || resultUploadCustomContacts.isLoading}
          selectedList={selectedList}
          selectedTags={selectedTags}
          showList={!listId}
          showTag={false}
          setActiveState={setActiveState}
          setFinalData={setFinalData}
          setSelectedList={setSelectedList}
          setSelectedTags={setSelectedTags}
          handleClose={closeModal}
          handleBack={handleSetOptionBack}
          handleNext={handleSetOptionNext}
        />
      )}
      {activeState === UploadStep.Confirmation && !isUnsubscribe && (
        <Confirmation
          confirmationCheck={inputConfirmationCheck}
          error={submitErr}
          setConfirmationCheck={setInputConfirmationCheck}
          handleClose={closeModal}
          handleNext={handleConfirmationNext}
          isLoading={resultUploadContacts.isLoading || resultUploadCustomContacts.isLoading}
          setActiveState={() => handleSetOptionBack()}
          csvLength={csvLength}
        />
      )}
      {activeState === UploadStep.Review && (
        <Review
          error={submitErr}
          isLoading={resultUploadContacts.isLoading || resultUploadCustomContacts.isLoading}
          handleClose={closeModal}
          report={reports}
          setActiveState={setActiveState}
          handleNext={handleReviewNext}
          previousStep={isUnsubscribe ? UploadStep.SetOptionList : UploadStep.Confirmation}
        />
      )}
    </Modal>
  )
}

UploadContact.defaultProps = {
  isUnsubscribe: false,
}

export default UploadContact
